import { GetServerSideProps, NextPage } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'
import { observer } from 'mobx-react'
import { useEffect, useState } from 'react'
import { getCompanyLogo, getStaticStores, useStores } from '@/utils'
import { IArticleBase, ICompanyBase, Language } from '@/types'
import { DefaultPageTemplate } from '@components-v2/templates/DefaultPageTemplate'
import { MyHead } from '@components-v2/organisms/MyHead'
import { ProfileContentTemplate } from '@components-v2/templates/ProfileContentTemplate'
import { CompanyProfileTabPageContainer } from '@components-v2/organisms/CompanyProfileTabPageContainer'
import { CompanyProfilePageHeader } from '@components-v2/organisms/CompanyProfilePageHeader'
import { ProfilePageSection } from '@components-v2/molecules/ProfilePageSection'
import { CompanyProfileArticles } from '@components-v2/organisms/CompanyProfileArticles'
import { Button2 } from '@components-v2/atoms/Button2'
import styles from './index.module.scss'

type Props = {
  company: ICompanyBase
  staticCompanyArticles: IArticleBase[]
  staticHasNextPage: boolean
}

const NUM_OF_ITEMS = 10

const ArticlesOfCompany: NextPage<Props> = observer(({ company, staticCompanyArticles, staticHasNextPage }) => {
  const { t } = useTranslation(['p'])
  const { companies, viewer } = useStores()
  const companyInstance = companies.createCompanyInstance(company)
  const title = t('のストーリー一覧', { name: company.name })
  let description = ''
  if (company?.aboutUs) {
    const descriptionPrefix = company?.aboutUs.substring(0, 130)
    const descriptionSuffix = company?.aboutUs.length > 130 ? '...' : ''
    description = `${descriptionPrefix}${descriptionSuffix}`
  }

  const hasDoneLoadMore = companies.companyArticles[company.slug] > staticCompanyArticles

  const [isFetching, setIsFetching] = useState(false)

  const fetchCompanyArticles = async () => {
    if (isFetching) {
      return
    }

    setIsFetching(true)
    await companies.fetchCompanyArticles({
      slug: company.slug,
      limit: hasDoneLoadMore ? NUM_OF_ITEMS : NUM_OF_ITEMS * 2,
      shouldRefresh: !hasDoneLoadMore,
    })
    setIsFetching(false)
  }

  useEffect(() => {
    companies.updateHasNextCompanyArticlesPage(company.slug, staticHasNextPage)
  }, [])

  useEffect(() => {
    if (!company || !companyInstance) {
      return
    }

    if (viewer.isInitialized && viewer.isSignedIn) {
      companyInstance.fetch()
    }
  }, [viewer.isInitialized, company])

  return (
    <DefaultPageTemplate withContainer={false}>
      <MyHead title={title} description={description} image={getCompanyLogo(company, true)} />
      <ProfileContentTemplate header={<CompanyProfilePageHeader company={companyInstance} />}>
        <CompanyProfileTabPageContainer company={companyInstance}>
          <div className={styles.container}>
            <div className={styles.content}>
              <ProfilePageSection title={t('ストーリー')}>
                <CompanyProfileArticles
                  articles={hasDoneLoadMore ? companies.companyArticles[company.slug] : staticCompanyArticles}
                  isCompanyMember={viewer.viewer?.isCompanyMember(company.slug)}
                  companySlug={company.slug}
                />
              </ProfilePageSection>
            </div>
            {companies.hasNextCompanyArticlesPage[company.slug] && (
              <div className={styles.button}>
                <Button2
                  size='md'
                  hierarchy='secondary-gray'
                  componentType='button'
                  isLoading={isFetching}
                  onClick={fetchCompanyArticles}
                >
                  {t('+ Load More')}
                </Button2>
              </div>
            )}
          </div>
        </CompanyProfileTabPageContainer>
      </ProfileContentTemplate>
    </DefaultPageTemplate>
  )
})

export const getServerSideProps: GetServerSideProps = async ({ locale, params }) => {
  const { companies, viewer } = getStaticStores()
  await viewer.changeLanguage(locale as Language)
  const companySlug = params.id as string
  const [company, companyArticlesOutput] = await Promise.all([
    companies.fetchCompany({ slug: companySlug, limitOfJob: 0 }),
    companies.fetchCompanyArticles({ slug: companySlug, limit: NUM_OF_ITEMS, shouldRefresh: true }),
  ])

  if (!company) {
    return {
      notFound: true,
    }
  }

  const staticCompanyArticles = companyArticlesOutput.data.articles

  return {
    props: {
      company,
      staticCompanyArticles,
      staticHasNextPage: companies.hasNextCompanyArticlesPage[companySlug],
      forcedTheme: 'light',
      ...(await serverSideTranslations(locale, ['global', 'p', 'companies-users-[id]', 'companies-[id]', 'articles'])),
    },
  }
}

export default ArticlesOfCompany
